import { motion } from 'framer-motion';
import {
  FaRoute, FaTicketAlt, FaHotel, FaPassport, FaWhatsapp, FaCheckCircle,
} from 'react-icons/fa';
import PageHero from '../../components/PageHero/PageHero';
import {
  services, hajjPackages, hajjCampaign, umrahPackages,
  WA_LINK, COMPANY,
} from '../../data/data';
import masjidBg from '../../assets/masjibNew.jpg';
import './Services.css';

const SERVICE_ICONS = { FaRoute, FaTicketAlt, FaHotel, FaPassport };

export default function Services() {
  return (
    <>
      <PageHero
        title="Our"
        highlight="Services"
        subtitle="Air ticketing, hotel booking, visa processing, and bespoke Hajj & Umrah packages — all under one roof."
        bgImage={masjidBg}
      />

      {/* ── Core Services ── */}
      <section className="svc-section section-pad">
        <div className="container">
          <div className="section-header">
            <p className="section-label">What We Do</p>
            <h2 className="section-title">Tours & Travel <span>Services</span></h2>
          </div>
          <div className="svc-full-grid">
            {services.map((svc, i) => {
              const Icon = SERVICE_ICONS[svc.icon];
              return (
                <motion.div
                  key={svc.id}
                  className="svc-full-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                >
                  <div className="svc-full-card__icon">
                    <Icon size={32} />
                  </div>
                  <div>
                    <h3 className="svc-full-card__title">{svc.title}</h3>
                    <p  className="svc-full-card__desc">{svc.desc}</p>
                    <a href={WA_LINK} target="_blank" rel="noreferrer" className="svc-full-card__link">
                      Enquire on WhatsApp →
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Umrah Packages ── */}
      <section className="umrah-section section-pad" id="umrah">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Sacred Pilgrimage</p>
            <h2 className="section-title">Umrah <span>Packages</span></h2>
            <p className="section-desc">
              All-inclusive spiritual journeys to Makkah and Madinah with return flight ticket, Saudi visa, hotel accommodation, and ground transportation.
            </p>
          </div>

          <div className="umrah-grid">
            {umrahPackages.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                className={`pkg-card ${i === 1 ? 'pkg-card--featured' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.15 }}
              >
                <span className="pkg-card__badge">All-Inclusive</span>
                <p className="pkg-card__eyebrow">Umrah Pilgrimage</p>
                <h3 className="pkg-card__name">{pkg.name}</h3>
                <p className="pkg-card__intro">{pkg.subtitle}</p>

                <div className="pkg-price-banner">
                  <span className="pkg-price-label">Package Fare</span>
                  <strong className="pkg-price-val">{pkg.price}</strong>
                </div>

                <ul className="pkg-card__features">
                  {pkg.features.map((f, j) => (
                    <li key={j}>
                      <FaCheckCircle className="pkg-check" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`https://api.whatsapp.com/send?phone=${COMPANY.whatsapp}&text=Hello%20AL-AMANAT%2C%20I%20am%20interested%20in%20booking%20the%20${encodeURIComponent(pkg.name)}%20Umrah%20package.%20Please%20share%20the%20registration%20details.`}
                  target="_blank"
                  rel="noreferrer"
                  className={i === 1 ? 'btn-primary' : 'btn-outline'}
                >
                  <FaWhatsapp /> Book This Package
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hajj Packages ── */}
      <section className="hajj-section section-pad" id="hajj">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Hajj {hajjCampaign.year}</p>
            <h2 className="section-title">Hajj <span>Packages</span></h2>
            <p className="section-desc">
              Registration is selling fast. Make your initial payment on or before {hajjCampaign.paymentDeadline} to guarantee your place.
            </p>
          </div>

          <div className="hajj-travel-dates" aria-label="Hajj travel dates">
            <div><span>Departure</span><strong>{hajjCampaign.departure}</strong></div>
            <div><span>Return</span><strong>{hajjCampaign.returnDate}</strong></div>
            <div><span>Payment deadline</span><strong>{hajjCampaign.paymentDeadline}</strong></div>
          </div>

          <div className="hajj-grid">
            {hajjPackages.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                className={`hajj-card ${pkg.highlight ? 'hajj-card--featured' : ''}`}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.15 }}
              >
                {pkg.badge && <span className="hajj-badge">{pkg.badge}</span>}
                <p className="hajj-card__eyebrow">🕋 Hajj Pilgrimage</p>
                <h3 className="hajj-card__name">{pkg.name}</h3>
                <p className="hajj-card__intro">A complete, guided journey with room options to match your preference.</p>

                {/* Prices */}
                <div className="hajj-prices">
                  {pkg.options.map((opt, j) => (
                    <div key={j} className="hajj-price-row">
                      <div>
                        <span className="hajj-price-label">{opt.label}</span>
                        {opt.note && <p className="hajj-price-note">{opt.note}</p>}
                      </div>
                      <strong className="hajj-price-val">{opt.price}</strong>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <ul className="hajj-features">
                  {pkg.features.map((f, j) => (
                    <li key={j}>
                      <FaCheckCircle className="check-icon" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className={pkg.highlight ? 'btn-primary' : 'btn-outline'}
                >
                  <FaWhatsapp /> Book This Package
                </a>
              </motion.div>
            ))}
          </div>

          <div className="hajj-arrangements">
            <div className="hajj-arrangements__heading">
              <p className="section-label">Tailor Your Journey</p>
              <h3>Special <span>Arrangements</span></h3>
            </div>
            <div className="hajj-arrangements__grid">
              {hajjCampaign.specialArrangements.map((item) => (
                <div className="hajj-arrangement" key={item.title}>
                  <span className="hajj-arrangement__number">0{hajjCampaign.specialArrangements.indexOf(item) + 1}</span>
                  <div><h4>{item.title}</h4><p>{item.detail}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
